import DOMPurify from 'dompurify';

/**
 * Comprehensive CSP (Content Security Policy) utilities
 * Handles violation reporting, content sanitization, and CSP-compliant DOM operations
 */

// ==================== TYPES ====================

interface CSPViolation {
  'document-uri': string;
  referrer: string;
  'violated-directive': string;
  'effective-directive': string;
  'original-policy': string;
  disposition: string;
  'blocked-uri': string;
  'line-number': number;
  'column-number': number;
  'source-file': string;
  'status-code': number;
  'script-sample': string;
}

interface CSPViolationReport {
  'csp-report': CSPViolation;
}

// ==================== VIOLATION REPORTING ====================

/**
 * Initialize CSP violation reporting
 */
export const initCSPReporting = (): void => {
  // Listen for CSP violations
  document.addEventListener('securitypolicyviolation', (event) => {
    const violation: CSPViolation = {
      'document-uri': event.documentURI,
      referrer: event.referrer,
      'violated-directive': event.violatedDirective,
      'effective-directive': event.effectiveDirective,
      'original-policy': event.originalPolicy,
      disposition: event.disposition,
      'blocked-uri': event.blockedURI,
      'line-number': event.lineNumber,
      'column-number': event.columnNumber,
      'source-file': event.sourceFile,
      'status-code': event.statusCode,
      'script-sample': event.sample || '',
    };

    reportCSPViolation(violation);
  });
};

/**
 * Report CSP violation to monitoring service
 */
const reportCSPViolation = async (violation: CSPViolation): Promise<void> => {
  try {
    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.warn('CSP Violation:', violation);
    }

    // In production, send to your monitoring service
    if (process.env.NODE_ENV === 'production') {
      const reportData: CSPViolationReport = {
        'csp-report': violation
      };

      // Send to your CSP violation endpoint
      await fetch('/api/csp-violations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(reportData),
      });
    }
  } catch (error) {
    console.error('Failed to report CSP violation:', error);
  }
};

// ==================== CONTENT SANITIZATION ====================

/**
 * Sanitizes HTML content to be CSP-compliant
 * @param content - The HTML content to sanitize
 * @returns Sanitized HTML content
 */
export const sanitizeHTML = (content: string): string => {
  return DOMPurify.sanitize(content, {
    ALLOWED_TAGS: [
      'div', 'span', 'p', 'br', 'strong', 'em', 'u', 'b', 'i',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li',
      'table', 'thead', 'tbody', 'tr', 'td', 'th',
      'a', 'img'
    ],
    ALLOWED_ATTR: [
      'href', 'src', 'alt', 'title', 'class', 'id', 'style',
      'target', 'rel'
    ],
    FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover']
  });
};

/**
 * Creates a safe element for CSP-compliant content insertion
 * @param content - The HTML content to insert
 * @param elementType - The type of element to create (default: 'div')
 * @returns HTMLElement with sanitized content
 */
export const createSafeElement = (content: string, elementType: string = 'div'): HTMLElement => {
  const element = document.createElement(elementType);
  element.innerHTML = sanitizeHTML(content);
  return element;
};

/**
 * Safely sets content in a window (replaces document.write)
 * @param targetWindow - The window to set content in
 * @param content - The content to set
 */
export const safeSetWindowContent = (targetWindow: Window, content: string): void => {
  if (targetWindow && targetWindow.document) {
    const sanitizedContent = sanitizeHTML(content);
    targetWindow.document.body.innerHTML = sanitizedContent;
  }
};

/**
 * Validates and sanitizes external URLs
 * @param url - The URL to validate
 * @returns Sanitized URL or null if invalid
 */
export const sanitizeURL = (url: string): string | null => {
  try {
    const parsedURL = new URL(url);
    // Only allow https and http protocols
    if (parsedURL.protocol === 'https:' || parsedURL.protocol === 'http:') {
      return parsedURL.toString();
    }
    return null;
  } catch {
    return null;
  }
};

// ==================== LEGACY COMPATIBILITY ====================

/**
 * Legacy CSPUtils object for backward compatibility
 * @deprecated Use individual named exports instead
 */
export const CSPUtils = {
  sanitizeHTML,
  createSafeElement,
  safeSetWindowContent,
  sanitizeURL
};

/**
 * Default export for backward compatibility
 * @deprecated Use individual named exports instead
 */
export default {
  initCSPReporting,
  sanitizeHTML,
  createSafeElement,
  safeSetWindowContent,
  sanitizeURL,
  CSPUtils
};
